import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { OFF_SEASON_LABELS } from '../constants/positions';
import { VIDEO_CONTENT_TYPES } from '../constants/videoContentTypes';
import { getVideoMeta } from '../utils/videoUtils';
import { AthleteProfileShell } from '../components/ProfileShell';
import Button from '../components/Button';
import BillingAthletePicker from '../components/BillingAthletePicker';
import { useBillingTarget, billingBody } from '../lib/billingTarget';
function VideoSlot({ category, slot, extraFields, videos, onSave, onDelete, apiUrl, onUploadComplete }) {
  const video = videos.find(v => v.category === category && v.slot_number === slot) || {};
  const [url, setUrl] = useState(video.video_url || '');
  const [label, setLabel] = useState(video.label || '');
  const [combineDate, setCombineDate] = useState(video.combine_date || '');
  const [combineLocation, setCombineLocation] = useState(video.combine_location || '');
  const [contentType, setContentType] = useState(video.content_type || '');
  const [context, setContext] = useState(video.context_label || '');
  const [clipTag, setClipTag] = useState(video.clip_tag || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const v = videos.find(v2 => v2.category === category && v2.slot_number === slot) || {};
    setUrl(v.video_url || '');
    setLabel(v.label || '');
    setCombineDate(v.combine_date || '');
    setCombineLocation(v.combine_location || '');
    setContentType(v.content_type || '');
    setContext(v.context_label || '');
    setClipTag(v.clip_tag || '');
  }, [videos, category, slot]);

  const saveAll = (overrides = {}) => {
    onSave(category, slot, {
      video_url: url,
      label: label || undefined,
      combine_date: combineDate || undefined,
      combine_location: combineLocation || undefined,
      content_type: contentType || undefined,
      context_label: context || undefined,
      clip_tag: clipTag || undefined,
      ...overrides,
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('slot_number', slot);
    if (label) formData.append('label', label);
    if (combineDate) formData.append('combine_date', combineDate);
    if (combineLocation) formData.append('combine_location', combineLocation);
    if (contentType) formData.append('content_type', contentType);
    if (context) formData.append('context_label', context);
    if (clipTag) formData.append('clip_tag', clipTag);
    try {
      await axios.post(`${apiUrl}/videos/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      onUploadComplete();
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Upload failed');
      setTimeout(() => setUploadError(''), 5000);
    }
    setUploading(false);
    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isUploadedVideo = video.video_type === 'upload';
  const meta = getVideoMeta(url);

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {extraFields === 'off_season' && (
          <select
            className="form-field__select"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => { if (url) saveAll(); }}
            style={{ width: '200px', fontSize: '13px', flexShrink: 0 }}
            aria-label={`${category} video ${slot} type`}
          >
            <option value="">Type...</option>
            {OFF_SEASON_LABELS.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        )}
        {extraFields === 'combine' && (
          <input
            type="text"
            className="form-field__input"
            value={combineLocation}
            onChange={(e) => setCombineLocation(e.target.value)}
            onBlur={() => { if (url) saveAll(); }}
            placeholder="Location"
            aria-label={`Combine video ${slot} location`}
            style={{ width: '200px', fontSize: '13px', flexShrink: 0 }}
          />
        )}
        {isUploadedVideo ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {video.thumbnail_url && (
              <img
                src={video.thumbnail_url}
                alt="Thumbnail"
                style={{ width: '64px', height: '36px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
              />
            )}
            <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>
              {video.video_title || 'Uploaded video'}
            </span>
          </div>
        ) : (
          <>
            <input
              type="url"
              className="form-field__input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={() => {
                if (url !== (video.video_url || '')) saveAll();
              }}
              placeholder="Paste video URL (YouTube, Hudl, etc.)"
              aria-label={`${category} video ${slot} URL`}
              style={{ flex: 1 }}
            />
            {meta && meta.platform === 'youtube' && meta.thumbnail_url && (
              <img
                src={meta.thumbnail_url}
                alt="Preview"
                style={{ width: '64px', height: '36px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
              />
            )}
            <span style={{ fontSize: '12px', color: '#94a3b8', flexShrink: 0 }}>or</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </>
        )}
        {video.id && (
          <Button
            variant="danger"
            size="sm"
            style={{ flexShrink: 0 }}
            onClick={() => onDelete(video.id)}
          >
            Remove
          </Button>
        )}
      </div>
      {uploadError && (
        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{uploadError}</div>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
        <select
          className="form-field__select"
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
          onBlur={() => { if (url) saveAll(); }}
          aria-label={`${category} video ${slot} content type`}
          style={{ width: '200px', fontSize: '13px', flexShrink: 0 }}
        >
          <option value="">Content type...</option>
          {VIDEO_CONTENT_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <input
          type="text"
          className="form-field__input"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          onBlur={() => { if (url) saveAll(); }}
          placeholder="Context (e.g., Regional Final)"
          aria-label={`${category} video ${slot} context`}
          style={{ width: '200px', fontSize: '13px', flexShrink: 0 }}
        />
        <input
          type="text"
          className="form-field__input"
          value={clipTag}
          onChange={(e) => setClipTag(e.target.value)}
          onBlur={() => { if (url) saveAll(); }}
          placeholder="Jersey #, role, or event"
          aria-label={`${category} video ${slot} clip tag`}
          style={{ width: '200px', fontSize: '13px', flexShrink: 0 }}
        />
      </div>
      {extraFields === 'combine' && (
        <div style={{ marginTop: '6px' }}>
          <input
            type="date"
            className="form-field__input"
            value={combineDate}
            onChange={(e) => setCombineDate(e.target.value)}
            onBlur={() => { if (url) saveAll(); }}
            aria-label={`Combine video ${slot} date`}
            style={{ width: '200px', fontSize: '13px' }}
          />
        </div>
      )}
    </div>
  );
}

export default function VideosPage() {
  const { user, API_URL } = useAuth();
  const { tier, limit } = useEntitlements();
  // Server-resolved, not inferred from local data — used only to hide the
  // "coaches see fewer of these on Free" notices for Pro athletes (for whom
  // they're not true). Strict equality: an unresolved/unauthenticated tier is
  // `undefined` here and in the OPEN default is 'pro', so both fail generous
  // and hide the restriction rather than falsely warn a Pro athlete.
  const isFree = tier === 'free';
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [videos, setVideos] = useState([]);
  const [visibleSlots, setVisibleSlots] = useState({ in_season: 1, off_season: 1, combine: 1, personal_statement: 1 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [personalVideoUrl, setPersonalVideoUrl] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [previewModal, setPreviewModal] = useState(null);
  const [stagedVideos, setStagedVideos] = useState([]);
  const [placingVideoId, setPlacingVideoId] = useState(null);
  const [placeCategory, setPlaceCategory] = useState('in_season');
  const [placeSlot, setPlaceSlot] = useState(1);

  const BILLING_URL = `${window.location.origin}/billing`;
  // Whose plan the upgrade modal buys — for a guardian acting on an athlete,
  // the athlete's (see lib/billingTarget.js).
  const billingTarget = useBillingTarget();
  const billingTargetId = billingTarget.athleteUserId;

  // Server truth: one flat total across all categories. null === unlimited
  // (Pro). Must short-circuit before any `>=` comparison — treating null as 0
  // would block Pro athletes from uploading, the exact failure
  // _enforce_video_capacity guards against on the backend.
  const videoCap = limit('videos');

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchVideos();
    fetchProfile();
    fetchStagedVideos();
  }, []);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setPreviewModal(null); };
    if (previewModal) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [previewModal]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/videos/me`, getAxiosConfig());
      const data = response.data;
      setVideos(data);
      // Set visible slots to max(1, existing video count) per category
      const counts = { in_season: 0, off_season: 0, combine: 0 };
      data.forEach(v => { if (counts[v.category] !== undefined) counts[v.category]++; });
      setVisibleSlots({
        in_season: Math.max(1, counts.in_season),
        off_season: Math.max(1, counts.off_season),
        combine: Math.max(1, counts.combine)
      });
    } catch (error) {
      console.log('Error fetching videos:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        setPersonalVideoUrl(response.data.personal_video_url || '');
        if (response.data.profile_url) setProfileUrl(response.data.profile_url);
      }
    } catch (error) {
      console.log('Error fetching profile:', error);
    }
  };

  const fetchStagedVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/videos/staged`, getAxiosConfig());
      setStagedVideos(response.data || []);
    } catch (error) {
      console.log('Error fetching staged videos:', error);
    }
  };

  const handlePlaceVideo = async (stagedId, category, slotNumber) => {
    try {
      await axios.post(`${API_URL}/videos/staged/${stagedId}/assign`, {
        category,
        slot_number: slotNumber
      }, getAxiosConfig());
      showMsg('Video placed');
      fetchVideos();
      fetchStagedVideos();
      setPlacingVideoId(null);
    } catch (error) {
      showMsg(error.response?.data?.detail || 'Error placing video', 'error');
    }
  };

  const handleDismissVideo = async (stagedId) => {
    try {
      await axios.delete(`${API_URL}/videos/staged/${stagedId}`, getAxiosConfig());
      showMsg('Video dismissed');
      fetchStagedVideos();
    } catch (error) {
      showMsg('Error dismissing video', 'error');
    }
  };

  const savePersonalVideoUrl = async (valueOverride) => {
    const value = valueOverride !== undefined ? valueOverride : personalVideoUrl;
    try {
      await axios.post(`${API_URL}/athletes/profile`, { personal_video_url: value }, getAxiosConfig());
      showMsg('Saved');
    } catch (error) {
      showMsg('Error saving', 'error');
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleSave = async (category, slot, data) => {
    try {
      await axios.post(`${API_URL}/videos/`, {
        category,
        slot_number: slot,
        ...data
      }, getAxiosConfig());
      showMsg('Saved');
      fetchVideos();
    } catch (error) {
      showMsg('Error saving video', 'error');
    }
  };

  const handleDelete = async (videoId) => {
    if (!videoId) return;
    try {
      await axios.delete(`${API_URL}/videos/${videoId}`, getAxiosConfig());
      showMsg('Video removed');
      fetchVideos();
    } catch (error) {
      showMsg('Error deleting video', 'error');
    }
  };

  const handleAddAnother = (category) => {
    // Total-count gate, not per-category: the server tracks one cap across
    // every category (see _enforce_video_capacity). null short-circuits here
    // before the comparison ever runs, so Pro never hits the modal.
    if (videoCap !== null && videos.length >= videoCap) {
      setShowUpgradeModal(true);
      return;
    }
    setVisibleSlots(prev => ({ ...prev, [category]: prev[category] + 1 }));
  };

  // Real, current behavior (backend/app/routers/videos.py:309-317): a coach
  // viewing this athlete's profile sees off-season/combine videos only if
  // the athlete is Pro — they aren't hidden from the athlete, only from
  // coaches. That's a video-visibility rule, not a claim about whether the
  // athlete's *profile* is paid to be found at all. Gated on tier (not
  // rendered for Pro, where it isn't true) rather than shown unconditionally
  // as it was before.
  const notVisibleToCoachesBadge = isFree ? (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
      fontSize: '11px', fontWeight: 600, backgroundColor: '#fef3c7', color: '#92400e',
      marginLeft: '8px'
    }}>
      Not visible to coaches on Free
    </span>
  ) : null;

  const addAnotherLink = (category) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => handleAddAnother(category)}
      style={{ marginTop: '4px' }}
    >
      + Add another highlight
    </Button>
  );

  const renderCategory = (title, category, extraFields, badge, desc) => (
    <div className="settings-section">
      <h2 className="settings-section__title">
        {title}
        {badge}
      </h2>
      <p className="settings-section__desc">{desc}</p>
      {Array.from({ length: visibleSlots[category] }, (_, i) => i + 1).map(slot => (
        <VideoSlot
          key={`${category}_${slot}`}
          category={category}
          slot={slot}
          extraFields={extraFields}
          videos={videos}
          onSave={handleSave}
          onDelete={handleDelete}
          apiUrl={API_URL}
          onUploadComplete={() => { fetchVideos(); fetchStagedVideos(); }}
        />
      ))}
      {addAnotherLink(category)}
    </div>
  );

  return (
    <AthleteProfileShell activeTab="Videos">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <p className="settings-section__desc" style={{ marginBottom: '16px' }}>
        {videoCap === null
          ? 'Unlimited videos'
          : `${videos.length} of ${videoCap} videos used`}
      </p>

      {stagedVideos.length > 0 && (
        <div className="settings-section" style={{ borderLeft: '3px solid var(--color-primary)', paddingLeft: '16px' }}>
          <h2 className="settings-section__title">Staged Videos</h2>
          <p className="settings-section__desc">These videos were uploaded but not yet placed in a category. Assign them to a slot or dismiss.</p>
          {stagedVideos.map(sv => (
            <div key={sv.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
              {sv.thumbnail_url && (
                <img src={sv.thumbnail_url} alt="Thumbnail" style={{ width: '64px', height: '36px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
              )}
              <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500, flex: 1, minWidth: '120px' }}>
                {sv.video_title || sv.video_url || 'Uploaded video'}
              </span>
              {placingVideoId === sv.id ? (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <select
                    className="form-field__select"
                    value={placeCategory}
                    onChange={(e) => setPlaceCategory(e.target.value)}
                    style={{ fontSize: '12px', width: '120px' }}
                  >
                    <option value="in_season">In-Season</option>
                    <option value="off_season">Off-Season</option>
                    <option value="combine">Combine</option>
                  </select>
                  <select
                    className="form-field__select"
                    value={placeSlot}
                    onChange={(e) => setPlaceSlot(Number(e.target.value))}
                    style={{ fontSize: '12px', width: '70px' }}
                  >
                    {/* Slot numbering is just an organizational index within a
                        category, not a paywall cap (the server no longer has a
                        per-category max to draw one from) — offer every slot
                        already on screen plus the next new one. */}
                    {Array.from({ length: visibleSlots[placeCategory] + 1 }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>Slot {n}</option>
                    ))}
                  </select>
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={() => handlePlaceVideo(sv.id, placeCategory, placeSlot)}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPlacingVideoId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={() => { setPlacingVideoId(sv.id); setPlaceCategory('in_season'); setPlaceSlot(1); }}
                  >
                    Place
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDismissVideo(sv.id)}
                  >
                    Dismiss
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">Personal Video</h2>
        <p className="settings-section__desc">Your main highlight or intro video. This is featured on your public profile.</p>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              id="personal_video_url"
              type="url"
              value={personalVideoUrl}
              onChange={(e) => setPersonalVideoUrl(e.target.value)}
              onBlur={savePersonalVideoUrl}
              className="form-field__input"
              placeholder="Paste video URL (YouTube, Hudl, etc.)"
              style={{ flex: 1 }}
            />
            {(() => {
              const m = getVideoMeta(personalVideoUrl);
              return m && m.platform === 'youtube' && m.thumbnail_url ? (
                <img src={m.thumbnail_url} alt="Preview" style={{ width: '64px', height: '36px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
              ) : null;
            })()}
            {personalVideoUrl && (
              <Button
                variant="danger"
                size="sm"
                style={{ flexShrink: 0 }}
                onClick={() => {
                  setPersonalVideoUrl('');
                  savePersonalVideoUrl('');
                }}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2 className="settings-section__title">Personal Statement</h2>
        <p className="settings-section__desc">A direct message to coaches — a professional intro, not a highlight reel.</p>
        <VideoSlot
          category="personal_statement"
          slot={1}
          extraFields={null}
          videos={videos}
          onSave={handleSave}
          onDelete={handleDelete}
          apiUrl={API_URL}
          onUploadComplete={() => { fetchVideos(); fetchStagedVideos(); }}
        />
      </div>

      {renderCategory(
        'In-Season Highlights',
        'in_season',
        null,
        // Real, current behavior (videos.py:309-317): a coach sees only the
        // first in-season video unless the athlete is Pro. Video-level coach
        // visibility, not a claim about the athlete's profile being found at
        // all — and only shown to Free, where it's true.
        isFree ? (
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
            fontSize: '11px', fontWeight: 600, backgroundColor: '#dbeafe', color: '#1e40af',
            marginLeft: '8px'
          }}>
            1 visible to coaches on Free
          </span>
        ) : null,
        'Paste links to your game highlights (YouTube, Hudl, etc.). Coaches see all of these on Pro — on Free, coaches see only the first.'
      )}

      {renderCategory(
        'Off-Season Highlights',
        'off_season',
        'off_season',
        notVisibleToCoachesBadge,
        'Training, camp, and workout videos. Visible to coaches on Pro.'
      )}

      {renderCategory(
        'Combine Videos',
        'combine',
        'combine',
        notVisibleToCoachesBadge,
        'Upload your combine performance videos with date and location.'
      )}

      {showUpgradeModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowUpgradeModal(false)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', textAlign: 'center'
          }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 16px',
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1a202c', marginBottom: '8px' }}>Upgrade Your Plan</h2>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: 1.5 }}>
              Unlock more video slots, and coaches see all of your highlights instead of just the first.
            </p>
            <BillingAthletePicker target={billingTarget} style={{ textAlign: 'left' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Button
                variant="accent"
                fullWidth
                disabled={billingLoading || billingTargetId == null}
                onClick={async () => {
                  setBillingLoading(true);
                  try {
                    const res = await axios.post(
                      `${BILLING_URL}/checkout/create-session`,
                      billingBody(user, billingTargetId, { price_key: 'monthly' }),
                    );
                    window.location.href = res.data.checkout_url;
                  } catch (err) {
                    setMessage('Error starting checkout');
                    setTimeout(() => setMessage(''), 3000);
                    setBillingLoading(false);
                  }
                }}
              >
                {billingLoading ? 'Loading...' : 'Monthly — $19.99/mo'}
              </Button>
              <Button
                variant="accent"
                fullWidth
                disabled={billingLoading || billingTargetId == null}
                onClick={async () => {
                  setBillingLoading(true);
                  try {
                    const res = await axios.post(
                      `${BILLING_URL}/checkout/create-session`,
                      billingBody(user, billingTargetId, { price_key: 'annual' }),
                    );
                    window.location.href = res.data.checkout_url;
                  } catch (err) {
                    setMessage('Error starting checkout');
                    setTimeout(() => setMessage(''), 3000);
                    setBillingLoading(false);
                  }
                }}
              >
                {billingLoading ? 'Loading...' : 'Annual — $199.99/yr (Save 17%)'}
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              style={{ marginTop: '16px' }}
              onClick={() => setShowUpgradeModal(false)}
            >
              Maybe later
            </Button>
          </div>
        </div>
      )}
      {previewModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setPreviewModal(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '90vw', height: '90vh', backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <Button onClick={() => setPreviewModal(null)} aria-label="Close preview" variant="secondary" iconOnly
              style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
              &times;
            </Button>
            <iframe
              src={previewModal === 'card' ? `/athlete/${user?.uid || '0'}/${profileUrl}/card` : `/athlete/${user?.uid || '0'}/${profileUrl}`}
              title={previewModal === 'card' ? 'Player Card Preview' : 'Public Profile Preview'}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      )}
    </AthleteProfileShell>
  );
}
